// ═══════════════════════════════════════════════════════════════════════════════
// ERROR PARSING UTILITIES
// Bridges your error system with React Query hooks
// ═══════════════════════════════════════════════════════════════════════════════

import { AxiosError } from "axios";

// Import from your existing error system
import {
  AppError,
  ApiError as ApiErrorClass,
  // NetworkError,
  // TimeoutError,
  // AuthenticationError,
  // AuthorizationError,
  RateLimitError,
  // CancellationError,
  isAppError,
} from "@/core/errors/classes";

import {
  ErrorCategory,
  ErrorSeverity,
  type ValidationFieldError,
} from "@/core/errors/types";
interface ApiErrorResponseData {
  message?: string;
  code?: string;
  errors?: Record<string, string[]>;
  requestId?: string;
}
// ═══════════════════════════════════════════════════════════════════════════════
// TYPE DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * ApiError type for React Query
 * Can be AppError or AxiosError (for backward compatibility)
 */
export type ApiError = AppError | AxiosError<ApiErrorResponseData>;

/**
 * Parsed error with all useful information
 */
export interface ParsedError {
  // ─────────────────────────────────────────────────────────────────────────
  // Basic Information
  // ─────────────────────────────────────────────────────────────────────────

  /** User-friendly error message */
  message: string;

  /** Technical error message (for logging) */
  technicalMessage: string;

  /** HTTP status code (0 for non-HTTP errors) */
  statusCode: number;

  /** Application error code */
  code: string;

  // ─────────────────────────────────────────────────────────────────────────
  // Classification
  // ─────────────────────────────────────────────────────────────────────────

  /** Error category */
  category: ErrorCategory;

  /** Error severity */
  severity: ErrorSeverity;

  // ─────────────────────────────────────────────────────────────────────────
  // Boolean Flags
  // ─────────────────────────────────────────────────────────────────────────

  isNetworkError: boolean;
  isTimeoutError: boolean;
  isCancelledError: boolean;
  isAuthError: boolean;
  isAuthorizationError: boolean;
  isValidationError: boolean;
  isNotFoundError: boolean;
  isRateLimitError: boolean;
  isServerError: boolean;
  isRetryable: boolean;

  // ─────────────────────────────────────────────────────────────────────────
  // Validation Errors
  // ─────────────────────────────────────────────────────────────────────────

  /** Field errors: { fieldName: ['error1', 'error2'] } */
  fieldErrors: Record<string, string[]>;

  /** Flat field errors: { fieldName: 'first error' } */
  flatFieldErrors: Record<string, string>;

  /** Fields with errors */
  errorFields: string[];

  /** Total error count */
  errorCount: number;

  // ─────────────────────────────────────────────────────────────────────────
  // Additional Info
  // ─────────────────────────────────────────────────────────────────────────

  /** Retry after seconds (rate limiting) */
  retryAfterSeconds?: number;

  /** Request ID for support */
  requestId?: string;

  /** Original error object */
  originalError: unknown;
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PARSE FUNCTION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Parse any error into structured format
 */
export function parseApiError(error: unknown): ParsedError {
  // Handle AppError (from your error system)
  if (isAppError(error)) {
    return parseAppError(error);
  }

  // Handle AxiosError (fallback)
  if (isAxiosError(error)) {
    return parseAxiosError(error as AxiosError<ApiErrorResponseData>);
  }

  // Handle generic Error
  if (error instanceof Error) {
    return createParsedError({
      message: error.message,
      technicalMessage: error.message,
      statusCode: 500,
      code: "ERR_UNKNOWN",
      category: ErrorCategory.UNKNOWN,
      severity: ErrorSeverity.MEDIUM,
      originalError: error,
    });
  }

  // Handle unknown
  return createParsedError({
    message: "An unexpected error occurred",
    technicalMessage: String(error),
    statusCode: 500,
    code: "ERR_UNKNOWN",
    category: ErrorCategory.UNKNOWN,
    severity: ErrorSeverity.MEDIUM,
    originalError: error,
  });
}

/**
 * Parse AppError from your error system
 */
function parseAppError(error: AppError): ParsedError {
  const fieldErrors = extractFieldErrors(error);
  const flatFieldErrors = getFlatFieldErrors(fieldErrors);

  // Extract rate limit info
  let retryAfterSeconds: number | undefined;
  if (error instanceof RateLimitError) {
    retryAfterSeconds = error.retryAfterSeconds;
  }

  // Extract request ID
  let requestId: string | undefined;
  if (error instanceof ApiErrorClass) {
    requestId = error.requestId;
  } else if (error.context?.requestId) {
    requestId = error.context.requestId;
  }

  return {
    // Basic info
    message: error.getUserMessage(),
    technicalMessage: error.message,
    statusCode: error.statusCode,
    code: error.code,

    // Classification
    category: error.category,
    severity: error.severity,

    // Boolean flags
    isNetworkError: error.category === ErrorCategory.NETWORK,
    isTimeoutError: error.category === ErrorCategory.TIMEOUT,
    isCancelledError: error.category === ErrorCategory.CANCELLED,
    isAuthError: error.category === ErrorCategory.AUTHENTICATION,
    isAuthorizationError: error.category === ErrorCategory.AUTHORIZATION,
    isValidationError: error.category === ErrorCategory.VALIDATION,
    isNotFoundError: error.category === ErrorCategory.NOT_FOUND,
    isRateLimitError: error.category === ErrorCategory.RATE_LIMIT,
    isServerError: error.category === ErrorCategory.SERVER,
    isRetryable: error.isRetryable,

    // Validation
    fieldErrors,
    flatFieldErrors,
    errorFields: Object.keys(fieldErrors),
    errorCount: Object.values(fieldErrors).flat().length,

    // Additional
    retryAfterSeconds,
    requestId,
    originalError: error,
  };
}

/**
 * Parse AxiosError (fallback for non-transformed errors)
 */
function parseAxiosError(error: AxiosError<ApiErrorResponseData>): ParsedError {
  const response = error.response;
  const data = response?.data;
  const status = response?.status ?? 0;

  // Determine category
  let category: ErrorCategory = ErrorCategory.UNKNOWN;
  if (status === 0) {
    if (error.code === "ECONNABORTED" || error.code === "ERR_TIMEOUT") {
      category = ErrorCategory.TIMEOUT;
    } else if (error.code === "ERR_CANCELED") {
      category = ErrorCategory.CANCELLED;
    } else {
      category = ErrorCategory.NETWORK;
    }
  } else if (status === 401) {
    category = ErrorCategory.AUTHENTICATION;
  } else if (status === 403) {
    category = ErrorCategory.AUTHORIZATION;
  } else if (status === 404) {
    category = ErrorCategory.NOT_FOUND;
  } else if (status === 422) {
    category = ErrorCategory.VALIDATION;
  } else if (status === 429) {
    category = ErrorCategory.RATE_LIMIT;
  } else if (status >= 500) {
    category = ErrorCategory.SERVER;
  } else if (status >= 400) {
    category = ErrorCategory.CLIENT;
  }

  // Extract field errors
  const fieldErrors = data?.errors ?? {};

  // Get message
  const message = data?.message ?? error.message ?? "An error occurred";

  return createParsedError({
    message,
    technicalMessage: error.message,
    statusCode: status,
    code: data?.code ?? getErrorCode(status),
    category,
    severity: getSeverity(status),
    fieldErrors,
    retryAfterSeconds: extractRetryAfter(error),
    requestId: data?.requestId ?? response?.headers?.["x-request-id"],
    originalError: error,
    isRetryable: isRetryableStatus(status),
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

function createParsedError(params: {
  message: string;
  technicalMessage: string;
  statusCode: number;
  code: string;
  category: ErrorCategory;
  severity: ErrorSeverity;
  fieldErrors?: Record<string, string[]>;
  retryAfterSeconds?: number;
  requestId?: string;
  originalError: unknown;
  isRetryable?: boolean;
}): ParsedError {
  const fieldErrors = params.fieldErrors ?? {};
  const flatFieldErrors = getFlatFieldErrors(fieldErrors);

  return {
    message: params.message,
    technicalMessage: params.technicalMessage,
    statusCode: params.statusCode,
    code: params.code,
    category: params.category,
    severity: params.severity,
    isNetworkError: params.category === ErrorCategory.NETWORK,
    isTimeoutError: params.category === ErrorCategory.TIMEOUT,
    isCancelledError: params.category === ErrorCategory.CANCELLED,
    isAuthError: params.category === ErrorCategory.AUTHENTICATION,
    isAuthorizationError: params.category === ErrorCategory.AUTHORIZATION,
    isValidationError: params.category === ErrorCategory.VALIDATION,
    isNotFoundError: params.category === ErrorCategory.NOT_FOUND,
    isRateLimitError: params.category === ErrorCategory.RATE_LIMIT,
    isServerError: params.category === ErrorCategory.SERVER,
    isRetryable: params.isRetryable ?? false,
    fieldErrors,
    flatFieldErrors,
    errorFields: Object.keys(fieldErrors),
    errorCount: Object.values(fieldErrors).flat().length,
    retryAfterSeconds: params.retryAfterSeconds,
    requestId: params.requestId,
    originalError: params.originalError,
  };
}

function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === "object" &&
    error !== null &&
    "isAxiosError" in error &&
    error.isAxiosError === true
  );
}

function extractFieldErrors(error: AppError): Record<string, string[]> {
  const fieldErrors: Record<string, string[]> = {};

  if (error.validationErrors) {
    for (const { field, message } of error.validationErrors) {
      if (!fieldErrors[field]) fieldErrors[field] = [];
      fieldErrors[field].push(message);
    }
  }

  if (error instanceof ApiErrorClass && error.errors) {
    for (const [field, messages] of Object.entries(error.errors)) {
      if (!fieldErrors[field]) fieldErrors[field] = [];
      fieldErrors[field].push(...messages);
    }
  }

  return fieldErrors;
}

/**
 * Get flat field errors (first error per field)
 */
export function getFlatFieldErrors(
  fieldErrors: Record<string, string[]>,
): Record<string, string> {
  const flat: Record<string, string> = {};
  for (const [field, messages] of Object.entries(fieldErrors)) {
    if (messages.length > 0) {
      flat[field] = messages[0];
    }
  }
  return flat;
}

function getErrorCode(status: number): string {
  const codes: Record<number, string> = {
    400: "ERR_BAD_REQUEST",
    401: "ERR_UNAUTHORIZED",
    403: "ERR_FORBIDDEN",
    404: "ERR_NOT_FOUND",
    422: "ERR_VALIDATION",
    429: "ERR_RATE_LIMIT",
    500: "ERR_SERVER",
  };
  return codes[status] ?? "ERR_UNKNOWN";
}

function getSeverity(status: number): ErrorSeverity {
  if (status >= 500) return ErrorSeverity.HIGH;
  if (status === 401 || status === 403) return ErrorSeverity.MEDIUM;
  return ErrorSeverity.LOW;
}

function isRetryableStatus(status: number): boolean {
  return [0, 408, 429, 500, 502, 503, 504].includes(status);
}

function extractRetryAfter(error: AxiosError): number | undefined {
  const retryAfter = error.response?.headers?.["retry-after"];
  if (!retryAfter) return undefined;
  const seconds = parseInt(retryAfter, 10);
  return isNaN(seconds) ? undefined : seconds;
}

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get retry delay in milliseconds
 */
export function getRetryDelay(parsedError: ParsedError | null): number {
  if (!parsedError?.retryAfterSeconds) return 0;
  return parsedError.retryAfterSeconds * 1000;
}

// Re-exports
export { ErrorCategory, ErrorSeverity };
export type { ValidationFieldError };
